import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import api from "@/lib/api";
import {
  applyActionLocal,
  cacheCleans,
  getCachedCleans,
  enqueue,
  loadQueue,
  flushQueue,
} from "@/lib/offline";
import { todayNZ, fmtDMY } from "@/lib/format";
import { AccessBanner } from "@/components/AccessBanner";
import { DemoBanner } from "@/components/DemoBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Coffee,
  Sandwich,
  Play,
  RotateCcw,
  LogOut,
  ArrowLeft,
  Wifi,
  WifiOff,
  CheckCircle2,
  Check,
  HandHelping,
  CloudUpload,
  Sparkles,
  Plus,
  Loader2,
  TriangleAlert,
  CalendarDays,
  GanttChartSquare,
  CalendarPlus,
  AlertCircle,
  FileText,
  MessageSquare,
  PenLine,
  Minimize2,
  Maximize2,
  Lightbulb,
} from "lucide-react";
import { ArrowRightLeft } from "lucide-react";
import RoomCard from "./RoomCard";
import StripTaskCard from "./StripTaskCard";
import SignTimesheetCard from "./SignTimesheetCard";
import MyRosterDialog from "./MyRosterDialog";
import MyTimelineDialog from "./MyTimelineDialog";
import LeaveRequestDialog from "./LeaveRequestDialog";
import { WeeklyTimesheetDialog } from "@/components/WeeklyTimesheetDialog";
import { SignaturePad } from "@/components/SignaturePad";
import { MessagesPanel } from "@/components/MessagesPanel";
import { NewRequestDialog } from "@/components/RequestsPanel";
import { InstallAppButton } from "@/components/InstallAppButton";
import { MessageAckPopup } from "@/components/MessageAckPopup";
import { useQuery } from "@tanstack/react-query";

// Short, gentle two-tone chime via Web Audio (no asset needed) — used to nudge
// a cleaner the moment their lunch minimum is up.
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    [880, 1175].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch (e) { /* ignore */ }
}

export default function CleanerApp() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const date = todayNZ();
  const [cleans, setCleans] = useState(() => getCachedCleans(date) || []);
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(loadQueue().length);
  const [, setTick] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [library, setLibrary] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [extraName, setExtraName] = useState("");
  const [parentCleanId, setParentCleanId] = useState("standalone");
  const [adding, setAdding] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reportClean, setReportClean] = useState(null);
  const [issueCategory, setIssueCategory] = useState("");
  const [issueNote, setIssueNote] = useState("");
  const [reporting, setReporting] = useState(false);
  const [noLunch, setNoLunch] = useState(false);
  const [lunchLogged, setLunchLogged] = useState(false);
  const [activeBreak, setActiveBreak] = useState(null);
  const [holidayAsks, setHolidayAsks] = useState([]);
  const loadHolidayAsks = useCallback(async () => {
    if (!navigator.onLine) return;
    try { setHolidayAsks((await api.get("/my/holiday-requests")).data || []); } catch { /* offline / not a cleaner */ }
  }, []);
  useEffect(() => { loadHolidayAsks(); const id = setInterval(loadHolidayAsks, 60000); return () => clearInterval(id); }, [loadHolidayAsks]);
  const answerHoliday = async (req, answer) => {
    try {
      const { data } = await api.post(`/my/holiday-requests/${req.id}/respond`, { answer });
      (data.result === "yes" ? toast.success : toast.info)(t(data.message), { duration: 8000 });
      setHolidayAsks((l) => l.filter((x) => x.id !== req.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [shift, setShift] = useState(null);
  const [breakLeft, setBreakLeft] = useState(null);
  const [lunchStage, setLunchStage] = useState("counting"); // counting → decide → reminder → resting
  const [showRoster, setShowRoster] = useState(false);
  const [rosterAlerts, setRosterAlerts] = useState([]);
  const [rosterJump, setRosterJump] = useState(null);
  const loadRosterAlerts = useCallback(async () => {
    if (!navigator.onLine) return;
    try { setRosterAlerts((await api.get("/my/roster-alerts")).data || []); } catch { /* offline / not a cleaner */ }
  }, []);
  useEffect(() => { loadRosterAlerts(); const id = setInterval(loadRosterAlerts, 60000); return () => clearInterval(id); }, [loadRosterAlerts]);
  const ackRosterAlerts = async (openRoster) => {
    const first = rosterAlerts.flatMap((a) => a.dates || []).sort()[0] || null;
    setRosterAlerts([]);
    if (openRoster) { setRosterJump(first); setShowRoster(true); }
    try { await api.post("/my/roster-alerts/seen"); } catch { /* best effort */ }
  };
  const [showMyTimeline, setShowMyTimeline] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [compact, setCompact] = useState(() => localStorage.getItem("kleana_cleaner_compact") !== "0");
  const toggleCompact = () => setCompact((c) => { localStorage.setItem("kleana_cleaner_compact", c ? "0" : "1"); return !c; });
  const [showMessages, setShowMessages] = useState(false);
  const { data: unreadMsgs } = useQuery({
    queryKey: ["messages-unread-count"],
    queryFn: async () => (await api.get("/messages/unread-count")).data,
    refetchInterval: 45000,
    enabled: online,
  });
  const unreadMsgCount = unreadMsgs?.count || 0;
  const [tsSigned, setTsSigned] = useState(true);
  const [nudgeOff, setNudgeOff] = useState(false);
  const [startPrompt, setStartPrompt] = useState(null);
  const [clockOutPrompt, setClockOutPrompt] = useState(null);
  const [clockingOut, setClockingOut] = useState(false);
  const [roomTab, setRoomTab] = useState("active");
  const movedDoneRef = useRef({});
  const lunchChimedRef = useRef(false);
  const doneTimers = useRef({});
  const seenDoneRef = useRef(null);
  const listRef = useRef(cleans);
  listRef.current = cleans;

  const loadShift = useCallback(async () => {
    try {
      const { data } = await api.get(`/my/shift?date=${date}`);
      setShift(data);
      setNoLunch(!!data.no_lunch);
      setLunchLogged(!!data.lunch_logged);
      setActiveBreak(data.active_break || null);
    } catch { /* offline — keep last known */ }
  }, [date]);

  useEffect(() => { loadShift(); }, [loadShift]);

  const [unsignedWeeks, setUnsignedWeeks] = useState([]);
  const [signTask, setSignTask] = useState(null);
  const [signGate, setSignGate] = useState(null); // { clean } when a start was blocked
  const [gateSig, setGateSig] = useState("");
  const [gateBusy, setGateBusy] = useState(false);
  const [tsDate, setTsDate] = useState(null);
  const refreshTsSigned = useCallback(() => {
    api.get(`/my/timesheet?date=${date}`).then(({ data }) => setTsSigned(!!data.cleaner_signed_at)).catch(() => {});
    api.get(`/my/timesheet/unsigned`).then(({ data }) => { setUnsignedWeeks(data.weeks || []); setSignTask(data.sign_task || null); }).catch(() => {});
  }, [date]);
  useEffect(() => { refreshTsSigned(); }, [refreshTsSigned]);
  const openTimesheetFor = (d) => { setTsDate(d || null); setShowTimesheet(true); };
  const signGateWeek = async () => {
    const wk = unsignedWeeks[0];
    if (!wk || !gateSig) return;
    setGateBusy(true);
    try {
      await api.post(`/my/timesheet/sign`, { date: wk.week_start, signature: gateSig });
      const { data } = await api.get(`/my/timesheet/unsigned`);
      const left = data.weeks || [];
      setUnsignedWeeks(left);
      setGateSig("");
      toast.success(t("Timesheet signed"));
      if (!left.length) {
        const c = signGate?.clean;
        setSignGate(null);
        if (c) performAct(c, "start_clean");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGateBusy(false);
    }
  };
  const dow = new Date().getDay(); // 0=Sun..6=Sat
  const showSignNudge = !tsSigned && !nudgeOff && (dow === 5 || dow === 6 || dow === 0);
  // Poll entitlement so "BREAK DUE"/"LUNCH DUE" appears as hours accrue.
  useEffect(() => {
    const t = setInterval(loadShift, 30000);
    const wake = () => { if (document.visibilityState === "visible") loadShift(); };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", wake); window.removeEventListener("focus", wake); window.removeEventListener("online", wake); };
  }, [loadShift]);

  const endShiftBreak = useCallback(async () => {
    try {
      await api.post(`/my/break/end`, { date });
      setActiveBreak(null);
      loadShift();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [date, loadShift]);

  const cancelShiftBreak = useCallback(async () => {
    if (!navigator.onLine) { toast.error(t("You need to be online to undo a break")); return; }
    try {
      await api.post(`/my/break/cancel`, { date });
      setActiveBreak(null);
      setLunchLogged(false);
      toast.success(t("Cancelled — it won't be counted"));
      loadShift();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [date, loadShift, t]);

  // Rest breaks count DOWN to auto-end at 15 min. Lunch counts down to its 30-min
  // minimum, then Resume unlocks (lunch has no auto-end — it can run longer).
  useEffect(() => {
    if (!activeBreak) { setBreakLeft(null); return; }
    lunchChimedRef.current = false;
    let done = false;
    const tick = () => {
      if (activeBreak.type === "lunch") {
        const ms = new Date(activeBreak.min_ends_at) - new Date();
        setBreakLeft(ms > 0 ? Math.ceil(ms / 1000) : 0);
        if (ms <= 0 && !lunchChimedRef.current) {
          lunchChimedRef.current = true;
          playChime();
          setLunchStage("decide"); // pop the Finish / Continue choice
        }
        // optional manager-set lunch auto-end (off by default)
        if (activeBreak.ends_at && new Date(activeBreak.ends_at) - new Date() <= 0 && !done) {
          done = true; endShiftBreak(); toast.info(t("Lunch time's up — you're back on the clock."));
        }
      } else if (!activeBreak.ends_at) {
        // no auto-end configured: count up, cleaner ends it manually
        setBreakLeft(-Math.floor((new Date() - new Date(activeBreak.start)) / 1000));
      } else {
        const ms = new Date(activeBreak.ends_at) - new Date();
        if (ms <= 0) {
          setBreakLeft(0);
          if (!done) { done = true; endShiftBreak(); toast.info(t("Break time's up — you're back on the clock.")); }
        } else {
          setBreakLeft(Math.ceil(ms / 1000));
        }
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [activeBreak, endShiftBreak]);

  // Reset the lunch decision flow whenever a lunch starts/ends.
  useEffect(() => {
    if (activeBreak?.type !== "lunch") setLunchStage("counting");
  }, [activeBreak]);

  // If the cleaner chose to keep resting past the minimum, re-surface the
  // Finish / Continue choice every 5 minutes so a forgotten lunch keeps nudging.
  useEffect(() => {
    if (lunchStage !== "resting") return;
    const t = setInterval(() => { playChime(); setLunchStage("decide"); }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [lunchStage]);

  const breakStarting = useRef(false);
  const startShiftBreak = async (type) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to log a break"));
      return;
    }
    if (breakStarting.current) return; // double-tap guard
    breakStarting.current = true;
    try {
      const { data } = await api.post(`/my/break/start`, { date, type });
      setActiveBreak(data.active_break);
      if (type === "lunch") { setNoLunch(false); setLunchLogged(true); }
      loadShift();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      breakStarting.current = false;
    }
  };

  const toggleNoLunch = async (val) => {
    setNoLunch(val);
    try {
      await api.post(`/my/shift?date=${date}`, { no_lunch: val });
    } catch (e) {
      setNoLunch(!val);
      toast.error(t("Could not update — check your connection"));
    }
  };

  const refetch = useCallback(async () => {
    try {
      const { data } = await api.get(`/my/cleans?date=${date}`);
      setCleans(data);
      cacheCleans(date, data);
      setLoaded(true);
    } catch {
      const cached = getCachedCleans(date);
      if (cached) setCleans(cached);
      setLoaded(true);
    }
  }, [date]);

  // merge a server clean response in place, preserving derived fields that
  // only /my/cleans provides (my_role, primary_name, assist_names) so cards
  // don't lose their role or jump sections. roleOverride recomputes the role
  // when an action changed the user's assist membership.
  const mergeClean = useCallback((id, data, roleOverride) => {
    const merged = listRef.current.map((c) =>
      c.id === id
        ? {
            ...c,
            ...data,
            my_role: roleOverride || c.my_role,
            primary_name: data.primary_name ?? c.primary_name,
            assist_names: (data.assist_sessions || c.assist_sessions || [])
              .filter((a) => a.started_at)
              .map((a) => a.cleaner_name),
          }
        : c
    );
    setCleans(merged);
    cacheCleans(date, merged);
  }, [date]);

  const loadLibrary = useCallback(async () => {
    try {
      const [lib, cats] = await Promise.all([
        api.get("/task-library"),
        api.get("/issue-categories"),
      ]);
      setLibrary(lib.data);
      setCategories(cats.data);
    } catch (e) {
      // task library unavailable (offline) — fall back silently
    }
  }, []);

  const sync = useCallback(async () => {
    const res = await flushQueue();
    setPending(loadQueue().length);
    if (res.flushed > 0) {
      const conflict = (res.results || []).find(
        (r) => !r.ok && typeof r.error === "string" && r.error.toLowerCase().includes("claim")
      );
      if (conflict) toast.info(t("A room was already picked up by another cleaner."));
      (res.results || []).filter((r) => r.ok && r.handed_over_to).forEach((r) =>
        toast.info(t("Room {{room}} handed to {{name}}, who is still working — your time is saved as helper time", { room: r.room_number, name: r.handed_over_to }), { duration: 8000 }));
      await refetch();
    }
  }, [refetch, t]);

  // initial load + connectivity handlers
  useEffect(() => {
    if (navigator.onLine) sync().then(refetch).then(loadLibrary);
    else refetch();

    const goOnline = () => {
      setOnline(true);
      sync().then(refetch).then(loadLibrary);
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 1s ticker for live timers
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // periodic background sync when online. Also pull the latest board so manager-side
  // changes (e.g. a room stripped or key-returned by a manager) show up on the cleaner's
  // app without a manual reload. Skips the pull while actions are still queued so we
  // never clobber unsynced local work.
  useEffect(() => {
    if (!online) return;
    const id = setInterval(async () => {
      await sync();
      if (loadQueue().length === 0) refetch();
    }, 20000);
    return () => clearInterval(id);
  }, [online, sync, refetch]);

  const meId = user?.id;

  const findMyOpenTask = (excludeId) => {
    for (const c of listRef.current) {
      if (c.id === excludeId) continue;
      const st = c.session?.status;
      if (c.claimed_by === meId && (st === "in_progress" || st === "paused"))
        return { clean: c, kind: "primary" };
    }
    for (const c of listRef.current) {
      if (c.id === excludeId) continue;
      const mine = (c.assist_sessions || []).find((a) => a.cleaner_id === meId);
      if (mine && (mine.status === "in_progress" || mine.status === "paused"))
        return { clean: c, kind: "assist" };
    }
    return null;
  };

  const scrollToRoom = (id) => {
    // wait for the re-render / reorder, then keep the started room in view
    setTimeout(() => {
      const el = document.getElementById(`rc-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  };

  const performAct = (clean, action) => {
    const ts = new Date().toISOString();
    const updated = applyActionLocal(clean, action, ts);
    const next = listRef.current.map((c) => (c.id === clean.id ? updated : c));
    setCleans(next);
    cacheCleans(date, next);
    enqueue({ clean_id: clean.id, action, timestamp: ts });
    setPending(loadQueue().length);
    if (action === "start_lunch") { setNoLunch(false); setLunchLogged(true); } // logging a lunch clears the "no lunch" default
    if (action === "start_clean") scrollToRoom(clean.id);
    if (navigator.onLine) sync();
  };

  const act = (clean, action) => {
    if (action === "start_clean") {
      if (!isMgr && unsignedWeeks.length) {
        setSignGate({ clean });
        return;
      }
      const open = findMyOpenTask(clean.id);
      if (open) {
        setStartPrompt({ open, newClean: clean, newKind: "primary" });
        return;
      }
    }
    performAct(clean, action);
    if (action === "end_clean" && clean.is_refill_task) setClockOutPrompt(clean);
  };

  const doClockOut = async () => {
    if (!navigator.onLine) { toast.error(t("You need to be online to clock out")); return; }
    setClockingOut(true);
    try {
      const { data } = await api.post(`/my/clock-out?date=${date}`);
      toast.success(t("Clocked out for the day — finish time {{time}}", { time: data.finish }));
      setClockOutPrompt(null);
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClockingOut(false);
    }
  };

  const toggleChecklist = async (clean, itemId) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to tick off tasks"));
      return;
    }
    const next = listRef.current.map((c) =>
      c.id === clean.id
        ? { ...c, checklist: (c.checklist || []).map((it) => (it.id === itemId ? { ...it, checked: !it.checked } : it)) }
        : c
    );
    setCleans(next);
    cacheCleans(date, next);
    try {
      const { data } = await api.post(`/cleans/${clean.id}/checklist/${itemId}`);
      mergeClean(clean.id, data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const assistPerform = async (clean, action) => {
    try {
      const { data } = await api.post(`/cleans/${clean.id}/assist/${action}`);
      const mine = (data.assist_sessions || []).find((a) => a.cleaner_id === meId && !a.released);
      mergeClean(clean.id, data, mine ? "assisting" : "assist_available");
      if (action === "start") scrollToRoom(clean.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const assist = async (clean, action) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to assist"));
      return;
    }
    if (action === "start") {
      const open = findMyOpenTask(clean.id);
      if (open) {
        setStartPrompt({ open, newClean: clean, newKind: "assist" });
        return;
      }
    }
    await assistPerform(clean, action);
  };

  const confirmEndAndStart = async () => {
    if (!startPrompt) return;
    const { open, newClean, newKind } = startPrompt;
    setStartPrompt(null);
    if (open.kind === "assist") await assistPerform(open.clean, "end");
    else performAct(open.clean, "end_clean");
    if (newKind === "assist") await assistPerform(newClean, "start");
    else if (newKind === "continue") await continuePerform(newClean);
    else performAct(newClean, "start_clean");
  };

  const release = async (clean) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to reopen a room"));
      return;
    }
    try {
      await api.post(`/cleans/${clean.id}/release`);
      toast.success(t("Room {{room}} reopened for another cleaner", { room: clean.room_number }));
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const undoStart = async (clean) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to undo"));
      return;
    }
    try {
      await api.post(`/cleans/${clean.id}/undo-start`);
      toast.success(t("Room {{room}} reset — you can start the right room", { room: clean.room_number }));
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doDnd = async (clean) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to mark Do Not Disturb"));
      return;
    }
    try {
      const { data } = await api.post(`/cleans/${clean.id}/dnd`);
      mergeClean(clean.id, data);
      toast.success(t("Room {{room}} marked Do Not Disturb — no time recorded", { room: clean.room_number }));
      if (data.dnd_moved_stay_tasks?.length) {
        toast.info(t("Long stay tasks moved to tomorrow: {{list}}", { list: data.dnd_moved_stay_tasks.map((m) => m.name).join(", ") }));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const undoDnd = async (clean) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to undo"));
      return;
    }
    try {
      const { data } = await api.post(`/cleans/${clean.id}/dnd/undo`);
      mergeClean(clean.id, data);
      toast.success(t("Room {{room}} back on the board", { room: clean.room_number }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const stripTimer = async (clean, path) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online for the Strip rooms task"));
      return;
    }
    try {
      const { data } = await api.post(`/cleans/${clean.id}/${path}`);
      mergeClean(clean.id, data, "primary");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const toggleStripRoom = async (clean, roomCleanId, done) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to update stripping"));
      return;
    }
    try {
      const { data } = await api.post(`/strip-task/room`, { date, room_clean_id: roomCleanId, done });
      mergeClean(clean.id, data, "primary");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const keyReturnStripRoom = async (clean, roomCleanId, returned) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to update key return"));
      return;
    }
    try {
      await api.post(`/cleans/${roomCleanId}/key-return`, { returned });
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const strip = async (clean, done) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to update stripping"));
      return;
    }
    try {
      const { data } = await api.post(`/cleans/${clean.id}/strip`, { done });
      mergeClean(clean.id, data);
      toast.success(done ? t("Room {{room}} marked stripped", { room: clean.room_number }) : t("Room {{room}} strip undone", { room: clean.room_number }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const keyReturn = async (clean, returned) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to update key return"));
      return;
    }
    try {
      const { data } = await api.post(`/cleans/${clean.id}/key-return`, { returned });
      mergeClean(clean.id, data);
      toast.success(returned ? t("Room {{room}} — key returned", { room: clean.room_number }) : t("Room {{room}} — key return undone", { room: clean.room_number }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      refetch();
    }
  };

  const continuePerform = async (clean) => {
    try {
      await api.post(`/cleans/${clean.id}/continue`);
      toast.success(t("Room {{room}} reopened — keep cleaning", { room: clean.room_number }));
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const continueClean = async (clean) => {
    if (!navigator.onLine) {
      toast.error(t("You need to be online to continue"));
      return;
    }
    const open = findMyOpenTask(clean.id);
    if (open) {
      setStartPrompt({ open, newClean: clean, newKind: "continue" });
      return;
    }
    await continuePerform(clean);
  };

  const completeAllAndEnd = async (clean) => {
    let target = clean;
    if (navigator.onLine) {
      try {
        const { data } = await api.post(`/cleans/${clean.id}/checklist/check-all`);
        target = data;
        const next = listRef.current.map((c) => (c.id === clean.id ? { ...c, checklist: data.checklist } : c));
        setCleans(next);
        cacheCleans(date, next);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    performAct(target, "end_clean");
  };

  const completeAllAndEndAssist = async (clean) => {
    if (navigator.onLine) {
      try {
        const { data } = await api.post(`/cleans/${clean.id}/checklist/check-all`);
        mergeClean(clean.id, data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    await assistPerform(clean, "end");
  };

  const addExtra = async () => {
    const name = extraName.trim();
    if (!name) {
      toast.error(t("Enter a task name"));
      return;
    }
    if (!navigator.onLine) {
      toast.error(t("You need to be online to add a new task"));
      return;
    }
    setAdding(true);
    try {
      await api.post(`/my/extra-clean?date=${date}`, {
        name,
        est_minutes: 0,
        parent_clean_id: parentCleanId === "standalone" ? null : parentCleanId,
      });
      toast.success(t("Extra task added"));
      setAddOpen(false);
      setExtraName("");
      setParentCleanId("standalone");
      await refetch();
      await loadLibrary();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const reportIssue = async () => {
    if (!issueCategory) {
      toast.error(t("Choose an issue type"));
      return;
    }
    if (!navigator.onLine) {
      toast.error(t("You need to be online to report a problem"));
      return;
    }
    setReporting(true);
    try {
      await api.post(`/cleans/${reportClean.id}/issue`, {
        category: issueCategory,
        note: issueNote,
      });
      toast.success(t("Problem reported — the office will be notified when you finish this room"));
      setReportClean(null);
      setIssueCategory("");
      setIssueNote("");
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReporting(false);
    }
  };

  // A room is "done" for the current user when their own session is completed
  // (primary session for cleaners/assignees, or their assist session for
  // managers/assistants). Completed rooms sink to the bottom; everything else
  // keeps a stable order so cards don't jump while being stripped / worked on.
  const isDone = (c) => {
    const role = c.my_role || "primary";
    if (role === "assisting" || role === "assist_available") {
      const a = (c.assist_sessions || []).find((x) => x.cleaner_id === meId);
      return a?.status === "completed";
    }
    return c.session?.status === "completed";
  };
  const roomNum = (c) => { const m = String(c.room_number ?? "").match(/\d+/); return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER; };
  // Cleans (full) by room number, then services by room number, then extras/other tasks.
  const kindRank = (c) => (c.is_extra || c.is_general ? 2 : c.clean_type === "service" ? 1 : 0);
  const orderRooms = (arr) => arr.slice().sort((a, b) =>
    ((isDone(a) ? 1 : 0) - (isDone(b) ? 1 : 0)) || (kindRank(a) - kindRank(b))
    || ((a.is_priority ? 0 : 1) - (b.is_priority ? 0 : 1)) || (roomNum(a) - roomNum(b))
    || String(a.room_number ?? "").localeCompare(String(b.room_number ?? ""), undefined, { numeric: true }));
  const doneToBottom = orderRooms;

  // A finished room stays on the Active tab for ~10s, then moves to Completed.
  // Rooms already done on load go straight to Completed (no hold).
  useEffect(() => {
    const doneIds = cleans.filter((c) => !c.is_strip_task && isDone(c)).map((c) => c.id);
    const doneSet = new Set(doneIds);
    if (seenDoneRef.current === null) {
      doneIds.forEach((id) => { movedDoneRef.current[id] = true; });
    } else {
      doneIds.forEach((id) => {
        if (!movedDoneRef.current[id] && !doneTimers.current[id]) {
          doneTimers.current[id] = setTimeout(() => {
            movedDoneRef.current[id] = true;
            delete doneTimers.current[id];
            setTick((t) => t + 1);
          }, 10000);
        }
      });
    }
    Object.keys(movedDoneRef.current).forEach((id) => {
      if (!doneSet.has(id)) delete movedDoneRef.current[id];
    });
    Object.keys(doneTimers.current).forEach((id) => {
      if (!doneSet.has(id)) { clearTimeout(doneTimers.current[id]); delete doneTimers.current[id]; }
    });
    seenDoneRef.current = doneSet;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleans]);

  const isMgr = user?.role === "manager";
  const stripTask = cleans.find((c) => c.is_strip_task && (c.my_role || "primary") !== "assist_available");
  const myRooms = doneToBottom(cleans.filter((c) => !c.is_strip_task && (c.my_role || "primary") !== "assist_available"));
  const helpRooms = doneToBottom(cleans.filter((c) => !c.is_strip_task && c.my_role === "assist_available"));
  const total = myRooms.length;
  const done = myRooms.filter(isDone).length;
  // "Live" = somebody is working the room right now (primary running/paused, or any helper running).
  const isLive = (c) => {
    const st = c.session?.status;
    if (st === "in_progress" || st === "paused") return true;
    return (c.assist_sessions || []).some((a) => a.status === "in_progress" || a.status === "paused");
  };
  const myOpen = findMyOpenTask(null);
  const focusClean = !isMgr && myOpen ? myOpen.clean : null;
  const progressRooms = orderRooms(isMgr ? [] : [...myRooms, ...helpRooms].filter((c) => isLive(c) || c.id === focusClean?.id));
  const progressIds = new Set(progressRooms.map((c) => c.id));
  const activeRooms = myRooms.filter((c) => !movedDoneRef.current[c.id] && !progressIds.has(c.id));
  const completedRooms = myRooms.filter((c) => movedDoneRef.current[c.id] && !progressIds.has(c.id));
  const pendingHelpRooms = isMgr ? helpRooms : helpRooms.filter((c) => !progressIds.has(c.id));
  const listFor = (tab) => (tab === "completed" ? completedRooms : tab === "progress" ? progressRooms : activeRooms);
  // The task I'm working on stays pinned at the top of every tab until I end it.
  const shownRooms = [
    ...(focusClean ? [focusClean] : []),
    ...listFor(roomTab).filter((c) => c.id !== focusClean?.id),
  ];
  const focusIdRef = useRef(null);
  useEffect(() => {
    const id = focusClean?.id || null;
    if (id && id !== focusIdRef.current) setRoomTab("progress");
    focusIdRef.current = id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusClean?.id]);

  const cleanNetMin = (c) => {
    const s = c.session;
    if (!s?.started_at || !s?.ended_at) return null;
    let sec = (new Date(s.ended_at) - new Date(s.started_at)) / 1000;
    (s.breaks || []).forEach((b) => { if (b.start && b.end) sec -= (new Date(b.end) - new Date(b.start)) / 1000; });
    return Math.max(0, sec / 60);
  };
  const completedDurs = completedRooms.map(cleanNetMin).filter((v) => v != null);
  const completedAvg = completedDurs.length ? Math.round(completedDurs.reduce((a, b) => a + b, 0) / completedDurs.length) : null;

  // Manager "Clean Rooms" view: they aren't assigned rooms and don't take breaks here,
  // so hide the cleaner-only shift controls and split the day's rooms by real completion
  // (a room is Done once its clean is actually finished, regardless of who did it).
  const signDue = !isMgr && !!signTask?.due;
  const signInTodo = signDue && !signTask.signed;
  const signInDone = signDue && signTask.signed;
  const helpTodo = helpRooms.filter((c) => c.session?.status !== "completed");
  const helpDone = helpRooms.filter((c) => c.session?.status === "completed");

  return (
    <div className="min-h-screen bg-background pb-10">
      <DemoBanner />
      {/* Sticky header */}
      <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <div>
              <div className="font-display text-base font-extrabold leading-none">KlenaPro</div>
              <div className="text-[11px] text-muted-foreground">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallAppButton variant="ghost" iconOnly />
            <Button variant="ghost" size="icon" onClick={() => setShowRequest(true)} data-testid="cleaner-request-btn" title={t("Request a change or feature")}>
              <Lightbulb className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMessages(true)}
              data-testid="cleaner-messages-btn"
              title={t("Messages")}
              className="relative"
            >
              <MessageSquare className="h-4 w-4" />
              {unreadMsgCount > 0 && (
                <span
                  data-testid="cleaner-messages-badge"
                  className="absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-bold leading-4 text-white"
                >
                  {unreadMsgCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowTimesheet(true)}
              data-testid="my-timesheet-btn"
              title={t("My timesheet")}
            >
              <FileText className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMyTimeline(true)}
              data-testid="my-timeline-btn"
              title={t("My timeline")}
            >
              <GanttChartSquare className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowRoster(true)}
              data-testid="my-roster-btn"
              title={t("My roster")}
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowLeave(true)}
              data-testid="request-leave-btn"
              title={t("Request leave")}
            >
              <CalendarPlus className="h-4 w-4" />
            </Button>
            <LanguageSwitcher compact />
            <span
              data-testid="connectivity-indicator"
              className={`flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                online ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
              }`}
            >
              {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {online ? t("Online") : t("Offline")}
            </span>
            {user?.role === "manager" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/manager")}
                data-testid="back-to-manager"
                className="k-press gap-1.5"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> {t("Manager")}
              </Button>
            ) : (
              <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="cleaner-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {!online && (
          <div className="bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-white" data-testid="offline-banner">
            {t("Offline mode — your work is saved and will sync automatically.")}
          </div>
        )}
        {pending > 0 && (
          <button
            onClick={sync}
            data-testid="pending-sync-bar"
            className="flex w-full items-center justify-center gap-1.5 bg-blue-600 px-4 py-1.5 text-center text-xs font-semibold text-white"
          >
            <CloudUpload className="h-3.5 w-3.5" /> {t("{{count}} action(s) pending — tap to sync", { count: pending })}
          </button>
        )}
      </header>

      <AccessBanner />

      <main className={`mx-auto max-w-lg px-4 pt-5 ${compact ? "k-compact" : ""}`} data-testid="cleaner-main" data-compact={compact ? "1" : "0"}>
        <div className="mb-5 flex items-end justify-between">
          <div>
            <h1 className="font-display text-2xl font-extrabold">{t("Today's Rooms")}</h1>
            <p className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString("en-NZ", { timeZone: "Pacific/Auckland", weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleCompact}
            data-testid="cleaner-compact-toggle"
            title={compact ? t("Switch to large view") : t("Switch to compact view")}
            className="k-press mb-1 inline-flex items-center gap-1 self-center rounded-md border border-border bg-card px-2 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            {compact ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
            {compact ? t("Large") : t("Compact")}
          </button>
          <div className="text-right">
            <div className="font-display text-2xl font-extrabold">
              {isMgr ? `${helpDone.length}/${helpRooms.length}` : `${done}/${total}`}
            </div>
            <div className="label-caps text-muted-foreground">{t("Done")}</div>
          </div>
        </div>
        {rosterAlerts.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2.5" data-testid="roster-alert-banner">
            <CalendarDays className="h-4 w-4 text-indigo-700" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-indigo-900">{t("Your roster has been updated")}</div>
              <ul className="mt-0.5 text-xs text-indigo-800" data-testid="roster-alert-lines">
                {rosterAlerts.flatMap((a) => a.lines).slice(0, 6).map((ln, i) => <li key={i}>{ln}</li>)}
              </ul>
            </div>
            <span className="ml-auto flex gap-2">
              <Button size="sm" className="h-8" onClick={() => ackRosterAlerts(true)} data-testid="roster-alert-view">{t("View roster")}</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => ackRosterAlerts(false)} data-testid="roster-alert-dismiss">{t("OK")}</Button>
            </span>
          </div>
        )}
        {holidayAsks.map((r) => (
          <div key={r.id} className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid={`holiday-ask-${r.id}`}>
            <span className="text-sm font-semibold text-amber-900">
              {t("Can you work {{holiday}} ({{date}})?", { holiday: r.holiday_name, date: fmtDMY(r.date) })}
              {r.needed ? ` ${t("We need {{count}} cleaner(s).", { count: r.needed })}` : ""}
              {r.note ? ` ${r.note}` : ""}
            </span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => answerHoliday(r, "yes")} data-testid={`holiday-ask-yes-${r.id}`}>{t("Yes, I can")}</Button>
              <Button size="sm" variant="outline" className="h-8" onClick={() => answerHoliday(r, "no")} data-testid={`holiday-ask-no-${r.id}`}>{t("No")}</Button>
            </span>
          </div>
        ))}
        {cleans.filter((c) => c.handover && c.handover.to === meId && !c.handover.seen && c.session?.status !== "completed").map((c) => (
          <div key={`ho-${c.id}`} className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2.5" data-testid={`handover-banner-${c.room_number}`}>
            <ArrowRightLeft className="h-4 w-4 text-emerald-700" />
            <span className="text-sm font-semibold text-emerald-800">
              {t("Room {{room}} is now yours — {{name}} finished first. Tap End when you're done.", { room: c.room_number, name: c.handover.from_name })}
            </span>
            <Button size="sm" variant="outline" className="ml-auto h-8" data-testid={`handover-banner-dismiss-${c.room_number}`}
              onClick={async () => {
                const next = listRef.current.map((x) => (x.id === c.id ? { ...x, handover: { ...x.handover, seen: true } } : x));
                setCleans(next); cacheCleans(date, next);
                try { await api.post(`/cleans/${c.id}/handover/seen`); } catch { /* best effort */ }
              }}>{t("OK")}</Button>
          </div>
        ))}
        {unsignedWeeks.length > 0 && !isMgr && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="unsigned-banner">
            <PenLine className="h-4 w-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-800">
              {t("Your timesheet for the week ending {{date}} isn't signed — sign it before starting a task.", { date: fmtDMY(unsignedWeeks[0].week_end) })}
            </span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openTimesheetFor(unsignedWeeks[0].week_start)} data-testid="unsigned-banner-check" className="h-8">{t("Check times")}</Button>
              <Button size="sm" onClick={() => setSignGate({ clean: null })} data-testid="unsigned-banner-sign" className="h-8">{t("Sign now")}</Button>
            </div>
          </div>
        )}
        {showSignNudge && !isMgr && !unsignedWeeks.length && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2.5" data-testid="sign-nudge">
            <FileText className="h-4 w-4 text-indigo-700" />
            <span className="text-sm font-semibold text-indigo-800">{t("It's nearly payroll — please sign this week's timesheet.")}</span>
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" onClick={() => setShowTimesheet(true)} data-testid="sign-nudge-open" className="h-8">{t("Sign now")}</Button>
              <Button size="sm" variant="ghost" onClick={() => setNudgeOff(true)} data-testid="sign-nudge-dismiss" className="h-8">{t("Dismiss")}</Button>
            </div>
          </div>
        )}

        {(shift?.break_due || shift?.lunch_due) && !activeBreak && !isMgr && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="break-due-banner">
            <AlertCircle className="h-4 w-4 text-amber-700" />
            {shift?.break_due && (
              <span className="label-caps rounded bg-amber-500 px-2 py-0.5 text-white" data-testid="break-due-tag">{t("Break due")}</span>
            )}
            {shift?.lunch_due && (
              <span className="label-caps rounded bg-orange-500 px-2 py-0.5 text-white" data-testid="lunch-due-tag">{t("Lunch due")}</span>
            )}
            <span className="text-sm font-semibold text-amber-800">
              {shift?.lunch_due
                ? t("You're due a lunch break — take it, or tick that you skipped it below.")
                : t("You're entitled to a break — take it when you can.")}
            </span>
          </div>
        )}

        {!isMgr && (
        <div className="mb-3 rounded-lg border border-border p-3" data-testid="shift-break-controls">
          {activeBreak ? (
            <div className="flex flex-col items-center gap-3 py-1" data-testid="active-break-panel">
              <span className="flex items-center gap-2 text-sm font-bold text-amber-700">
                {activeBreak.type === "lunch" ? <Sandwich className="h-4 w-4" /> : <Coffee className="h-4 w-4" />}
                {activeBreak.type === "lunch" ? t("On lunch") : t("On break")}
              </span>

              {/* Prominent countdown timer, shown ABOVE the button */}
              {activeBreak.type === "lunch" ? (
                breakLeft > 0 ? (
                  <div className="flex flex-col items-center" data-testid="break-countdown">
                    <span className="font-mono text-4xl font-extrabold tabular-nums text-orange-600">
                      {Math.floor(breakLeft / 60)}:{String(breakLeft % 60).padStart(2, "0")}
                    </span>
                    <span className="label-caps text-xs text-muted-foreground">{t("until 30-min minimum")}</span>
                  </div>
                ) : (
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-semibold text-emerald-700" data-testid="break-countdown">
                    {t("30-min minimum reached")}
                  </span>
                )
              ) : (
                breakLeft != null && (
                  <div className="flex flex-col items-center" data-testid="break-countdown">
                    <span className="font-mono text-4xl font-extrabold tabular-nums text-amber-600">
                      {Math.floor(Math.abs(breakLeft) / 60)}:{String(Math.abs(breakLeft) % 60).padStart(2, "0")}
                    </span>
                    <span className="label-caps text-xs text-muted-foreground">{breakLeft < 0 ? t("on break · end when ready") : t("left · auto-ends")}</span>
                  </div>
                )
              )}

              {activeBreak.type === "lunch" ? (
                <Button
                  onClick={endShiftBreak}
                  disabled={breakLeft > 0}
                  data-testid="shift-break-resume"
                  className="k-press h-11 w-full gap-2 bg-amber-500 font-bold hover:bg-amber-600 disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" /> {t("Resume")}
                </Button>
              ) : breakLeft < 0 ? (
                <Button onClick={endShiftBreak} data-testid="shift-break-resume" className="k-press h-11 w-full gap-2 bg-amber-500 font-bold hover:bg-amber-600">
                  <RotateCcw className="h-4 w-4" /> {t("End break")}
                </Button>
              ) : (
                <span className="text-xs font-semibold text-muted-foreground">{t("Break auto-ends when the timer runs out")}</span>
              )}
              <button
                type="button"
                onClick={cancelShiftBreak}
                data-testid="shift-break-cancel"
                className="k-press text-xs font-semibold text-muted-foreground underline hover:text-foreground"
              >
                {activeBreak.type === "lunch" ? t("Pressed by accident? Cancel this lunch") : t("Pressed by accident? Cancel this break")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-auto text-xs font-semibold text-muted-foreground">
                  {t("Breaks")}{shift?.offset_minutes ? ` · ${10 + shift.offset_minutes}-min break, 30-min lunch` : ""}
                </span>
                <Button
                  variant="outline"
                  disabled={!shift?.can_start_break}
                  onClick={() => startShiftBreak("break")}
                  data-testid="shift-break-start"
                  className="k-press h-11 gap-2 border-amber-300 font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                >
                  <Coffee className="h-4 w-4" /> {t("Break")}
                </Button>
                <Button
                  variant="outline"
                  disabled={!shift?.can_start_lunch}
                  onClick={() => startShiftBreak("lunch")}
                  data-testid="shift-lunch-start"
                  className="k-press h-11 gap-2 border-orange-300 font-bold text-orange-700 hover:bg-orange-50 disabled:opacity-40"
                >
                  <Sandwich className="h-4 w-4" /> {t("Lunch")}
                </Button>
              </div>
              {shift?.can_start_break && !shift?.break_due && (
                <p className="text-xs text-muted-foreground" data-testid="early-break-note">
                  {t("You can take a break or lunch now — it's logged. A rest break only counts as paid once you've worked long enough to earn it; lunch is unpaid.")}
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {!isMgr && (
        <label
          className={`mb-5 flex items-center gap-2.5 rounded-lg border border-border bg-secondary/30 px-3 py-2.5 text-sm font-medium ${
            lunchLogged ? "opacity-60" : ""
          }`}
          data-testid="no-lunch-toggle"
        >
          <input
            type="checkbox"
            checked={lunchLogged ? false : noLunch}
            disabled={lunchLogged}
            onChange={(e) => toggleNoLunch(e.target.checked)}
            className="h-4 w-4 accent-primary disabled:cursor-not-allowed"
            data-testid="no-lunch-checkbox"
          />
          <Sandwich className="h-4 w-4 text-orange-600" />
          <span>
            {lunchLogged ? t("Lunch break logged for today") : t("I didn't take a lunch break today")}
          </span>
        </label>
        )}

        {stripTask && (
          <div className="mb-4">
            <StripTaskCard
              clean={stripTask}
              onStart={(c) => stripTimer(c, "start")}
              onPause={(c) => stripTimer(c, "pause")}
              onResume={(c) => stripTimer(c, "resume")}
              onEnd={(c) => stripTimer(c, "end")}
              onToggleRoom={toggleStripRoom}
              onKeyReturnRoom={keyReturnStripRoom}
            />
          </div>
        )}

        {(!isMgr || total > 0) && (loaded && total === 0 && !stripTask && !signDue && progressRooms.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed py-16 text-center">
            <CheckCircle2 className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {t("No rooms assigned to you today. Check with your manager.")}
            </p>
          </div>
        ) : (
          <>
            {(total > 0 || signDue || progressRooms.length > 0) && (
              <div className="mb-4 inline-flex w-full rounded-lg border border-border bg-card p-1" data-testid="cleaner-room-tabs">
                <button
                  onClick={() => setRoomTab("active")}
                  data-testid="cleaner-tab-active"
                  className={`k-press flex-1 rounded-md px-3 py-2 text-sm font-semibold ${roomTab === "active" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                >
                  {t("To do")} ({activeRooms.length + (signInTodo ? 1 : 0)})
                </button>
                {!isMgr && (
                  <button
                    onClick={() => setRoomTab("progress")}
                    data-testid="cleaner-tab-progress"
                    className={`k-press flex-1 rounded-md px-3 py-2 text-sm font-semibold ${roomTab === "progress" ? "bg-primary text-primary-foreground" : progressRooms.length ? "text-amber-700" : "text-muted-foreground"}`}
                  >
                    {t("In progress / Assist")} ({progressRooms.length})
                  </button>
                )}
                <button
                  onClick={() => setRoomTab("completed")}
                  data-testid="cleaner-tab-completed"
                  className={`k-press flex-1 rounded-md px-3 py-2 text-sm font-semibold ${roomTab === "completed" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                >
                  {t("Completed")} ({completedRooms.length + (signInDone ? 1 : 0)})
                </button>
              </div>
            )}
            {shownRooms.length === 0 && !(roomTab === "completed" ? signInDone : signInTodo) ? (
              <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-center" data-testid="cleaner-rooms-empty">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm text-muted-foreground">
                  {roomTab === "completed" ? t("No completed rooms yet.") : roomTab === "progress" ? t("Nothing in progress right now — rooms being cleaned (yours or a teammate's) show here so you can jump in and assist.") : t("All your rooms are done — nice work!")}
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="cleaner-rooms">
                {(roomTab === "completed" ? signInDone : signInTodo) && (
                  <SignTimesheetCard task={signTask} onSign={() => openTimesheetFor(signTask.week_start)} />
                )}
                {roomTab === "completed" && completedRooms.length > 0 && (
                  <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-800" data-testid="cleaner-completed-summary">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("{{count}} room(s) done", { count: completedRooms.length })}{completedAvg != null ? t(" · avg {{n}} min each", { n: completedAvg }) : ""}
                  </div>
                )}
                {shownRooms.map((c) => (
                  <RoomCard
                    key={c.id}
                    clean={c}
                    focused={c.id === focusClean?.id}
                    meId={user?.id}
                    onAct={act}
                    onReport={() => setReportClean(c)}
                    onToggle={toggleChecklist}
                    onAssist={assist}
                    onRelease={release}
                    onUndoStart={undoStart}
                    onContinue={continueClean}
                    onCompleteAllEnd={completeAllAndEnd}
                    onCompleteAllEndAssist={completeAllAndEndAssist}
                    onStrip={strip}
                    onKeyReturn={keyReturn}
                    onDnd={doDnd}
                    onUndoDnd={undoDnd}
                    isManager={user?.role === "manager"}
                  />
                ))}
              </div>
            )}
          </>
        ))}

        {pendingHelpRooms.length > 0 && (
          <div className="mt-8 space-y-4" data-testid="help-teammate">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <HandHelping className="h-4 w-4" />
              {isMgr ? t("Today's rooms ({{count}})", { count: helpRooms.length }) : t("Help a teammate ({{count}})", { count: pendingHelpRooms.length })}
            </div>
            {isMgr ? (
              <>
                {helpTodo.length === 0 && helpDone.length > 0 && (
                  <div className="flex items-center gap-3 rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-4" data-testid="mgr-day-complete-banner">
                    <CheckCircle2 className="h-8 w-8 shrink-0 text-emerald-600" />
                    <div>
                      <div className="font-display text-xl font-bold text-emerald-800">{t("All rooms done for today")}</div>
                      <div className="text-sm text-emerald-700">{t("{{count}} room(s) finished — nothing left to clean.", { count: helpDone.length })}</div>
                    </div>
                  </div>
                )}
                {helpTodo.length > 0 && (
                  <div className="space-y-4" data-testid="mgr-rooms-todo">
                    <div className="label-caps text-muted-foreground">{t("To clean ({{count}})", { count: helpTodo.length })}</div>
                    {helpTodo.map((c) => (
                      <RoomCard
                        key={c.id}
                        clean={c}
                        meId={user?.id}
                        onAct={act}
                        onReport={() => setReportClean(c)}
                        onToggle={toggleChecklist}
                        onAssist={assist}
                        onStrip={strip}
                        onKeyReturn={keyReturn}
                        onCompleteAllEndAssist={completeAllAndEndAssist}
                        isManager
                      />
                    ))}
                  </div>
                )}
                {helpDone.length > 0 && (
                  <div className="space-y-2" data-testid="mgr-rooms-done">
                    <div className="label-caps text-emerald-700">{t("Done today ({{count}})", { count: helpDone.length })}</div>
                    {helpDone.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5"
                        data-testid={`mgr-done-room-${c.room_number}`}
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                          <span className="font-bold">{c.room_number}</span>
                          <span className="truncate text-sm text-muted-foreground">{c.room_type}</span>
                        </div>
                        <div className="shrink-0 text-right text-xs text-muted-foreground">
                          {c.primary_name ? t("by {{name}}", { name: c.primary_name }) : t("Done")}
                          {cleanNetMin(c) != null ? ` · ${Math.round(cleanNetMin(c))}m` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              pendingHelpRooms.map((c) => (
                <RoomCard
                  key={c.id}
                  clean={c}
                  meId={user?.id}
                  onAct={act}
                  onReport={() => setReportClean(c)}
                  onToggle={toggleChecklist}
                  onAssist={assist}
                  onStrip={strip}
                  onKeyReturn={keyReturn}
                  onCompleteAllEndAssist={completeAllAndEndAssist}
                  isManager={user?.role === "manager"}
                />
              ))
            )}
          </div>
        )}

        {/* Add extra task */}
        <Button
          variant="outline"
          onClick={() => setAddOpen(true)}
          data-testid="add-extra-task-btn"
          className="k-press mt-5 h-14 w-full gap-2 border-dashed text-base font-semibold"
        >
          <Plus className="h-5 w-5" /> {t("Add extra task")}
        </Button>
      </main>

      <MyRosterDialog open={showRoster} onClose={() => { setShowRoster(false); setRosterJump(null); }} initialDate={rosterJump} />
      <MessageAckPopup enabled={online} />

      {/* Persistent lunch reminder — cannot be dismissed until lunch is ended,
          so cleaners can't forget to clock back on. Only shows for lunch (rest
          breaks auto-end). */}
      <Dialog open={activeBreak?.type === "lunch"}>
        <DialogContent
          className="max-w-sm text-center [&>button]:hidden"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="lunch-reminder-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-2xl">
              <Sandwich className="h-6 w-6 text-orange-500" /> {t("On lunch")}
            </DialogTitle>
            <DialogDescription>
              {lunchStage === "decide"
                ? t("Your 30-minute lunch is up. Finish lunch and get back to work, or continue your lunch?")
                : lunchStage === "reminder"
                ? t("No problem — enjoy the rest of your lunch. Just remember to tap End Lunch before you go back to work, or your break keeps counting.")
                : breakLeft > 0
                ? t("Enjoy your break. You can end lunch once the minimum time is up.")
                : t("Lunch time's up — tap End Lunch to get back on the clock.")}
            </DialogDescription>
          </DialogHeader>
          <div className="my-2 font-display text-5xl font-extrabold tabular-nums" data-testid="lunch-reminder-timer">
            {breakLeft != null
              ? `${Math.floor(Math.abs(breakLeft) / 60)}:${String(Math.abs(breakLeft) % 60).padStart(2, "0")}`
              : "—"}
          </div>
          {lunchStage === "decide" ? (
            <div className="flex flex-col gap-2">
              <Button
                onClick={endShiftBreak}
                data-testid="lunch-finish-btn"
                className="k-press h-14 w-full gap-2 bg-emerald-600 text-lg font-bold hover:bg-emerald-700"
              >
                <Play className="h-5 w-5" /> {t("Finish lunch")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setLunchStage("reminder")}
                data-testid="lunch-continue-btn"
                className="k-press h-12 w-full gap-2 text-base font-bold"
              >
                <Sandwich className="h-4 w-4" /> {t("Continue lunch")}
              </Button>
            </div>
          ) : lunchStage === "reminder" ? (
            <div className="flex flex-col gap-2">
              <div className="mb-1 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm font-semibold text-amber-800" data-testid="lunch-reminder-note">
                <TriangleAlert className="h-4 w-4 shrink-0" />
                {t("End Lunch before you start any work — it won't stop on its own.")}
              </div>
              <Button
                onClick={endShiftBreak}
                data-testid="lunch-reminder-end-now-btn"
                className="k-press h-14 w-full gap-2 bg-emerald-600 text-lg font-bold hover:bg-emerald-700"
              >
                <Play className="h-5 w-5" /> {t("End Lunch now")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setLunchStage("resting")}
                data-testid="lunch-stay-btn"
                className="k-press h-12 w-full gap-2 text-base font-bold"
              >
                {t("Stay on lunch")}
              </Button>
            </div>
          ) : (
            <Button
              onClick={endShiftBreak}
              disabled={breakLeft > 0}
              data-testid="lunch-reminder-end-btn"
              className="k-press h-14 w-full gap-2 bg-emerald-600 text-lg font-bold hover:bg-emerald-700"
            >
              <Play className="h-5 w-5" /> {t("End Lunch")}
            </Button>
          )}
          <button
            onClick={cancelShiftBreak}
            data-testid="lunch-reminder-cancel-btn"
            className="k-press mt-1 text-xs font-medium text-muted-foreground underline"
          >
            {t("Pressed by accident? Cancel this lunch")}
          </button>
        </DialogContent>
      </Dialog>
      <NewRequestDialog open={showRequest} onOpenChange={setShowRequest} />
      <Dialog open={showMessages} onOpenChange={setShowMessages}>
        <DialogContent className="max-w-lg" data-testid="cleaner-messages-dialog">
          <DialogHeader>
            <DialogTitle className="sr-only">{t("Messages")}</DialogTitle>
            <DialogDescription className="sr-only">{t("Messages are automatically translated to each person's language.")}</DialogDescription>
          </DialogHeader>
          <MessagesPanel />
        </DialogContent>
      </Dialog>
      <MyTimelineDialog open={showMyTimeline} onClose={() => setShowMyTimeline(false)} />
      <LeaveRequestDialog open={showLeave} onOpenChange={setShowLeave} />
      <WeeklyTimesheetDialog
        open={showTimesheet}
        onOpenChange={(o) => { setShowTimesheet(o); if (!o) { setTsDate(null); refreshTsSigned(); } }}
        mode="cleaner"
        date={tsDate || date}
      />

      <Dialog open={!!signGate} onOpenChange={(o) => { if (!o) { setSignGate(null); setGateSig(""); } }}>
        <DialogContent data-testid="sign-gate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PenLine className="h-5 w-5" /> {t("Sign last week's timesheet first")}</DialogTitle>
            <DialogDescription>
              {unsignedWeeks[0] && t("Before starting a new task, please sign your timesheet for the week ending {{date}}. Check your times first if you need to.", { date: fmtDMY(unsignedWeeks[0].week_end) })}
            </DialogDescription>
          </DialogHeader>
          {unsignedWeeks.length > 1 && (
            <p className="text-xs text-amber-700" data-testid="sign-gate-count">
              {t("{{count}} weeks still need your signature.", { count: unsignedWeeks.length })}
            </p>
          )}
          <SignaturePad onChange={setGateSig} testid="sign-gate-pad" />
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={() => openTimesheetFor(unsignedWeeks[0]?.week_start)}
              data-testid="sign-gate-check-times"
              className="gap-2"
            >
              <FileText className="h-4 w-4" /> {t("Check my times")}
            </Button>
            <Button onClick={signGateWeek} disabled={!gateSig || gateBusy} data-testid="sign-gate-submit" className="gap-2">
              {gateBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("Sign & start task")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!startPrompt} onOpenChange={(o) => !o && setStartPrompt(null)}>
        <DialogContent data-testid="one-task-dialog">
          <DialogHeader>
            <DialogTitle>{t("Finish your current task first")}</DialogTitle>
          <DialogDescription>
            {startPrompt && t("You're still working on {{current}}. You can only work on one task at a time. End it and start {{next}}?", {
              current: startPrompt.open.clean.is_extra ? startPrompt.open.clean.room_number : `${t("Room")} ${startPrompt.open.clean.room_number}`,
              next: startPrompt.newClean.is_extra ? startPrompt.newClean.room_number : `${t("Room")} ${startPrompt.newClean.room_number}`,
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => setStartPrompt(null)} data-testid="one-task-cancel">
            {t("Keep current task")}
          </Button>
          <Button onClick={confirmEndAndStart} data-testid="one-task-end-and-start" className="gap-2">
            {t("End & start new")}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!clockOutPrompt} onOpenChange={(o) => !o && setClockOutPrompt(null)}>
        <DialogContent data-testid="clock-out-dialog">
          <DialogHeader>
            <DialogTitle>{t("Clock out for the day?")}</DialogTitle>
            <DialogDescription>
              {t("Nice work — your trolley's refilled. Want to clock out for the day? Your finish time will be rounded to the nearest 15 minutes. You can keep working if you're not done.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setClockOutPrompt(null)} data-testid="clock-out-cancel">
              {t("Not yet")}
            </Button>
            <Button onClick={doClockOut} disabled={clockingOut} data-testid="clock-out-confirm" className="gap-2">
              {clockingOut && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("Clock me out")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-extra-dialog">
          <DialogHeader>
            <DialogTitle>{t("Add an extra task")}</DialogTitle>
            <DialogDescription>
              {t("e.g. spa area, guest laundry, extra cleaning. Pick one you've used before or type a new one.")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("Task name")}</Label>
              <Input
                value={extraName}
                onChange={(e) => setExtraName(e.target.value)}
                placeholder={t("Type a task, e.g. Cleaned spa area")}
                data-testid="extra-task-name-input"
              />
            </div>

            {library.length > 0 && (
              <div>
                <div className="label-caps mb-2 text-muted-foreground">{t("Previously used")}</div>
                <div className="flex flex-wrap gap-2" data-testid="task-library-chips">
                  {library.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setExtraName(name)}
                      data-testid={`library-chip-${name}`}
                      className={`k-press rounded-full border px-3 py-1.5 text-sm font-medium ${
                        extraName === name
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-secondary/50 hover:bg-secondary"
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{t("Part of a room? (optional)")}</Label>
              <Select value={parentCleanId} onValueChange={setParentCleanId}>
                <SelectTrigger data-testid="extra-parent-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standalone">{t("Standalone (not tied to a room)")}</SelectItem>
                  {cleans
                    .filter((c) => !c.is_extra && !c.is_general)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`extra-parent-${c.room_number}`}>
                        {t("Room")} {c.room_number}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("If tied to a room, this task's time is added to that room's total.")}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button onClick={addExtra} disabled={adding} data-testid="save-extra-task-btn" className="gap-2">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t("Add task")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report a problem dialog */}
      <Dialog open={!!reportClean} onOpenChange={(o) => !o && setReportClean(null)}>
        <DialogContent data-testid="report-issue-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TriangleAlert className="h-5 w-5 text-amber-500" />
              {t("Report a problem")}
            </DialogTitle>
            <DialogDescription>
              {reportClean &&
                t("{{room}}. The office is emailed automatically when you finish this room.", { room: reportClean.is_extra ? reportClean.room_number : `${t("Room")} ${reportClean.room_number}` })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("Issue type")}</Label>
              <Select value={issueCategory} onValueChange={setIssueCategory}>
                <SelectTrigger data-testid="issue-category-select">
                  <SelectValue placeholder={t("Select a problem")} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`issue-cat-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("Details (optional)")}</Label>
              <Textarea
                value={issueNote}
                onChange={(e) => setIssueNote(e.target.value)}
                placeholder={t("e.g. Bedside lamp bulb blown")}
                data-testid="issue-note-input"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReportClean(null)}>
              {t("Cancel")}
            </Button>
            <Button
              onClick={reportIssue}
              disabled={reporting}
              data-testid="submit-issue-btn"
              className="gap-2 bg-amber-500 hover:bg-amber-600"
            >
              {reporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <TriangleAlert className="h-4 w-4" />}
              {t("Report problem")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
